import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const STATUS_OPTIONS = ['trial', 'active', 'expired', 'cancelled'];

const STATUS_STYLE = {
  trial:     { background: '#eff6ff', color: '#1d4ed8' },
  active:    { background: '#f0fdf4', color: '#15803d' },
  expired:   { background: '#fef2f2', color: '#b91c1c' },
  cancelled: { background: '#f8fafc', color: '#475569' },
};

const STATUS_LABEL = {
  trial:     'ניסיון',
  active:    'פעיל',
  expired:   'פג תוקף',
  cancelled: 'בוטל',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('he-IL', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
}

export default function AdminDashboard({ onClose }) {
  const { session } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(null);
  const [editNotes, setEditNotes] = useState({}); // userId -> notes string
  const [filter, setFilter]   = useState('all');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setUsers(data);
    } catch (e) {
      setError('שגיאה בטעינת משתמשים: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const updateUser = async (userId, updates) => {
    setSaving(userId);
    try {
      await fetch(`/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      await fetchUsers();
    } finally {
      setSaving(null);
    }
  };

  const saveNotes = (userId) => {
    if (editNotes[userId] !== undefined) {
      updateUser(userId, { notes: editNotes[userId] });
      setEditNotes(prev => { const n = {...prev}; delete n[userId]; return n; });
    }
  };

  const filteredUsers = filter === 'all'
    ? users
    : users.filter(u => u.subscription_status === filter);

  const counts = users.reduce((acc, u) => {
    acc[u.subscription_status] = (acc[u.subscription_status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f1f5f9',
      fontFamily: "'Segoe UI', -apple-system, sans-serif",
      direction: 'rtl',
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onClose}
            style={{
              padding: '7px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              background: 'white',
              cursor: 'pointer',
              fontSize: 13,
              color: '#475569',
              fontWeight: 500,
            }}
          >
            ← חזרה לאפליקציה
          </button>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
            ניהול משתמשים
          </h1>
        </div>
        <button
          onClick={fetchUsers}
          style={{
            padding: '7px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            background: 'white',
            cursor: 'pointer',
            fontSize: 13,
            color: '#475569',
          }}
        >
          רענן
        </button>
      </div>

      <div style={{ padding: '24px' }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { key: 'all',       label: 'סה"כ',     count: users.length,         color: '#1e293b', bg: 'white' },
            { key: 'trial',     label: 'ניסיון',    count: counts.trial || 0,    color: '#1d4ed8', bg: '#eff6ff' },
            { key: 'active',    label: 'פעילים',    count: counts.active || 0,   color: '#15803d', bg: '#f0fdf4' },
            { key: 'expired',   label: 'פג תוקף',  count: counts.expired || 0,  color: '#b91c1c', bg: '#fef2f2' },
            { key: 'cancelled', label: 'בוטלו',    count: counts.cancelled || 0, color: '#475569', bg: '#f8fafc' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              style={{
                padding: '12px 20px',
                borderRadius: 10,
                border: filter === s.key ? `2px solid ${s.color}` : '1.5px solid #e2e8f0',
                background: s.bg,
                cursor: 'pointer',
                minWidth: 100,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 12, color: s.color, fontWeight: 500 }}>{s.label}</div>
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: 8, padding: '12px 16px', marginBottom: 16,
            color: '#b91c1c', fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {/* Table */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              טוען משתמשים...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
              אין משתמשים להצגה
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['אימייל', 'שם', 'סטטוס', 'סיום ניסיון', 'תשלום אחרון', 'הצטרף', 'הערות', '📦 אחזקות', 'פעולה'].map(h => (
                    <th key={h} style={{
                      padding: '10px 14px',
                      textAlign: 'right',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#64748b',
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, i) => {
                  const isSaving = saving === u.id;
                  const statusStyle = STATUS_STYLE[u.subscription_status] || STATUS_STYLE.trial;
                  const notes = editNotes[u.id] !== undefined ? editNotes[u.id] : (u.notes || '');

                  return (
                    <tr
                      key={u.id}
                      style={{
                        borderBottom: i < filteredUsers.length - 1 ? '1px solid #f1f5f9' : 'none',
                        opacity: isSaving ? 0.6 : 1,
                      }}
                    >
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#1e293b', direction: 'ltr', textAlign: 'right' }}>
                        {u.email}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#475569' }}>
                        {u.full_name || '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          ...statusStyle,
                          padding: '3px 10px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}>
                          {STATUS_LABEL[u.subscription_status] || u.subscription_status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatDate(u.trial_ends_at)}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatDate(u.last_payment_at)}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatDate(u.created_at)}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <input
                          value={notes}
                          onChange={e => setEditNotes(prev => ({ ...prev, [u.id]: e.target.value }))}
                          onBlur={() => saveNotes(u.id)}
                          onKeyDown={e => e.key === 'Enter' && saveNotes(u.id)}
                          placeholder="הוסף הערה..."
                          style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: '5px 8px',
                            fontSize: 12,
                            width: 140,
                            color: '#475569',
                            outline: 'none',
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title={u.has_holdings ? 'גישה לאחזקות מופעלת' : 'גישה לאחזקות מושבתת'}>
                          <input
                            type="checkbox"
                            checked={!!u.has_holdings}
                            disabled={isSaving}
                            onChange={e => updateUser(u.id, { has_holdings: e.target.checked })}
                            style={{ accentColor: '#7c3aed', width: 16, height: 16, cursor: 'pointer' }}
                          />
                        </label>
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <select
                          value={u.subscription_status}
                          disabled={isSaving}
                          onChange={e => updateUser(u.id, { subscription_status: e.target.value })}
                          style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: '5px 8px',
                            fontSize: 12,
                            color: '#1e293b',
                            cursor: 'pointer',
                            background: 'white',
                          }}
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
